import {
  BankAccountDepositSweeps,
  DepositSweepStatus,
} from '@/types/bankAccounts';
import { Card, Divider, Grid, Typography } from '@mui/material';
import { SVButton, SVLabeledText } from '../core';
import { useDepositSweep } from '@/hooks/requests/useBankAccounts';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { formatDateTimeFromUTC } from '@/utils/date';

type BankAccountDetailsDepositSweepsProps = {
  bankAccountId: string;
  depositSweep?: BankAccountDepositSweeps;
};

export default function BankAccountDetailsDepositSweep({
  bankAccountId,
  depositSweep,
}: BankAccountDetailsDepositSweepsProps) {
  const { depositSweepEnroll, depositSweepUneroll, isDepositSweepsPending } =
    useDepositSweep(bankAccountId);
  const isFirstTime = !depositSweep;
  const isEnrolled = depositSweep?.status === DepositSweepStatus.ENROLLED;

  const {
    status,
    tpDepositSweepId,
    enrolledAt,
    enrolledBy,
    unenrolledAt,
    unenrolledBy,
  } = depositSweep || {};

  async function handleDepositSweeps() {
    try {
      if (isEnrolled) {
        await depositSweepUneroll();
      } else {
        await depositSweepEnroll();
      }
      showToast({
        type: 'success',
        data: {
          content: `This account has successfully been ${isEnrolled ? 'unenrolled' : 'enrolled'} in the FDIC Enhanced Insurance Program`,
        },
      });
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        FDIC Enhanced Insurance Program
      </Typography>
      {isFirstTime ? (
        <SVLabeledText label="Status" text="N/A" />
      ) : (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <SVLabeledText label="Status" text={status} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <SVLabeledText
              label="Deposit Sweeps Account ID"
              text={tpDepositSweepId}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <SVLabeledText
              label="Enrolled At"
              text={formatDateTimeFromUTC(enrolledAt, true)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <SVLabeledText label="Enrolled By" text={enrolledBy?.fullName} />
          </Grid>
          {!isEnrolled && (
            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVLabeledText
                  label="Unenrolled At"
                  text={formatDateTimeFromUTC(unenrolledAt, true)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <SVLabeledText
                  label="Unenrolled By"
                  text={unenrolledBy?.fullName}
                />
              </Grid>
            </>
          )}
        </Grid>
      )}
      <Divider sx={{ mb: 2, mt: 2 }} />
      <SVButton
        loading={isDepositSweepsPending}
        variant="text"
        onClick={handleDepositSweeps}>
        {isEnrolled ? 'Unenroll' : 'Enroll'}
      </SVButton>
    </Card>
  );
}
